import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import ReviewManager from "../../components/ReviewManager";
import Select from "react-select";
import { Row, Col } from "react-bootstrap";
import logo from "../../images/logo.png";

// ✅ Reusable Yes/No toggle (like QualificationsPage)
const YesNoToggle = ({ name, value, onChange, label, disabled }) => {
  return (
    <div>
      <label className="d-block mb-1">{label}</label>
      <div className="btn-group" role="group">
        <input
          type="radio"
          className="btn-check"
          name={name}
          id={`${name}_yes`}
          value="Yes"
          checked={value === "Yes"}
          onChange={(e) => onChange(name, e.target.value)}
          disabled={disabled}
        />
        <label className="btn btn-outline-primary" htmlFor={`${name}_yes`}>
          Yes
        </label>

        <input
          type="radio"
          className="btn-check"
          name={name}
          id={`${name}_no`}
          value="No"
          checked={value === "No"}
          onChange={(e) => onChange(name, e.target.value)}
          disabled={disabled}
        />
        <label className="btn btn-outline-secondary" htmlFor={`${name}_no`}>
          No
        </label>
      </div>
    </div>
  );
};

const BASE_URL = process.env.REACT_APP_API_BASE_URL;
const INTERVIEW_TASK_ID = 31; // task id for interviews

const InterviewQuestionsPage = () => {
  const navigate = useNavigate();
  const { id: paramId } = useParams();
  const loggedInId = localStorage.getItem("userId");
  const userRole = localStorage.getItem("userRole");
  const isAdmin = userRole === "1";
  const userId = isAdmin && paramId ? paramId : loggedInId;

  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [readyForReview, setReadyForReview] = useState(false);

  // safe parser for iq_options
  const parseOptions = (raw) => {
    if (!raw) return [];
    if (Array.isArray(raw)) return raw;
    if (typeof raw === "object") return Object.values(raw);
    try {
      return JSON.parse(raw);
    } catch {
      return raw
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);
    }
  };

  const fetchQA = useCallback(async () => {
    if (!userId) return;
    try {
      const res = await axios.get(
        `${BASE_URL}/api/interviews/user/${userId}/${INTERVIEW_TASK_ID}`,
        {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        }
      );
      setQuestions(res.data.questions || []);
      setAnswers(res.data.answers || {});

      // ✅ preload review/complete state if already set
      if (
        res.data.taskStatus === "review" ||
        res.data.taskStatus === "completed"
      ) {
        setReadyForReview(true);
      }
    } catch (err) {
      console.error("❌ Failed to load interview questions + answers", err);
    }
  }, [userId]);

  useEffect(() => {
    fetchQA();
  }, [fetchQA]);

  const handleChange = (qid, value) => {
    setAnswers((prev) => ({ ...prev, [qid]: value }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      await axios.put(
        `${BASE_URL}/api/interviews/answers/${userId}/${INTERVIEW_TASK_ID}`,
        { answers },
        {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        }
      );
      alert("✔️ Saved!");
    } catch (err) {
      console.error("❌ Failed to save answers", err);
    }
  };

  const handleReviewToggle = async (e) => {
    const checked = e.target.checked;

    if (checked) {
      // Mark as Ready for Review
      if (
        !window.confirm("Are you sure you want to mark as Ready for Review?")
      ) {
        return;
      }
      try {
        await axios.put(
          `${BASE_URL}/api/interviews/review/${userId}/${INTERVIEW_TASK_ID}`,
          {},
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );
        setReadyForReview(true);
        alert("✔️ Interview marked as Ready for Review!");
      } catch (err) {
        console.error("❌ Failed to mark Ready for Review", err);
        alert("❌ Failed to update Ready for Review status.");
      }
    } else {
      // Only Admin can uncheck
      if (!isAdmin) return;
      if (!window.confirm("Reset interview back to Not Completed?")) return;

      try {
        await axios.put(
          `${BASE_URL}/api/interviews/review/reset/${userId}/${INTERVIEW_TASK_ID}`,
          {},
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );
        setReadyForReview(false);
        alert("✔️ Interview reset to Not Completed.");
      } catch (err) {
        console.error("❌ Failed to reset review", err);
        alert("❌ Failed to reset review status.");
      }
    }
  };

  const handleCancel = () => {
    navigate(isAdmin && paramId ? `/userhome/${paramId}` : "/userhome");
  };

  const renderInput = (q) => {
    let value = answers[q.iq_id] ?? "";
    const disabled = !isAdmin && readyForReview; // ✅ only lock for non-admins

    switch (q.iq_type) {
      case "text":
      case "number":
      case "date":
        return (
          <input
            type={q.iq_type}
            className="form-control"
            value={value}
            onChange={(e) => handleChange(q.iq_id, e.target.value)}
            disabled={disabled}
          />
        );

      case "textarea":
        return (
          <textarea
            className="form-control"
            value={value}
            onChange={(e) => handleChange(q.iq_id, e.target.value)}
            disabled={disabled}
          />
        );

      case "boolean":
        return (
          <YesNoToggle
            name={`q_${q.iq_id}`}
            value={value}
            onChange={(name, v) => handleChange(q.iq_id, v)}
            label={q.iq_question}
            disabled={disabled}
          />
        );

      case "select":
        return (
          <Select
            options={parseOptions(q.iq_options).map((o) => ({
              value: o,
              label: o,
            }))}
            value={value ? { value, label: value } : null}
            onChange={(opt) => handleChange(q.iq_id, opt?.value || "")}
            isDisabled={disabled}
          />
        );

      case "multiselect":
        return (
          <Select
            isMulti
            options={parseOptions(q.iq_options).map((o) => ({
              value: o,
              label: o,
            }))}
            value={
              Array.isArray(value)
                ? value.map((v) => ({ value: v, label: v }))
                : []
            }
            onChange={(opts) =>
              handleChange(
                q.iq_id,
                opts.map((o) => o.value)
              )
            }
            isDisabled={disabled}
          />
        );

      default:
        return null;
    }
  };

  return (
    <>
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center pt-3">
        <span style={{ fontSize: "18px", fontWeight: "500" }}>
          Interview Questions
        </span>
        <button className="btn btn-secondary" onClick={handleCancel}>
          Return
        </button>
      </div>

      <div className="form-wrapper my-4">
        {/* Logo Row */}
        <div className="row align-items-center mb-4">
          <div className="col-3 text-start">
            <img
              src={logo}
              alt="Company Logo"
              style={{ width: "150px", height: "auto" }}
            />
          </div>
          <div className="col-6 text-center">
            <h2 className="m-0">Interview Questions</h2>
          </div>
          <div className="col-3 text-end">
            <strong>New-Way Trucking Ltd</strong>
            <br />
            7725 48 St. SE, Calgary
            <br />
            Alberta, Canada T2C 2V3
          </div>
        </div>

        {/* Questions Grid */}
        <form onSubmit={handleSave}>
          <Row>
            {questions.map((q) => (
              <Col md={6} key={q.iq_id} className="mb-3">
                {q.iq_type === "boolean" ? (
                  renderInput(q) // Yes/No toggle
                ) : (
                  <>
                    <label className="form-label">{q.iq_question}</label>
                    {renderInput(q)}
                  </>
                )}
              </Col>
            ))}
          </Row>

          {/* Ready for Review */}
          <div className="form-check mt-4">
            <input
              type="checkbox"
              className="form-check-input"
              id="readyForReview"
              checked={readyForReview}
              onChange={handleReviewToggle}
              disabled={!isAdmin && readyForReview}
            />
            <label className="form-check-label" htmlFor="readyForReview">
              Ready for Review
            </label>
          </div>

          {/* Save button visible if not review OR if admin */}
          {(!readyForReview || isAdmin) && (
            <div className="d-flex justify-content-end mt-4">
              <button type="submit" className="btn btn-primary">
                Save Answers
              </button>
            </div>
          )}
        </form>

        <hr className="my-5" />

        {userId && (
          <ReviewManager
            taskName="interview"
            taskId={INTERVIEW_TASK_ID}
            userId={isAdmin && paramId ? paramId : userId}
            isAdmin={isAdmin}
          />
        )}
      </div>
    </>
  );
};

export default InterviewQuestionsPage;
